<template>
    <div>
        <div class="page-header">
            <div class="md-icon" @click="back">arrow_back</div>
            <div class="title">{{ $t('create') }} {{ entityName }}</div>
            <el-button type="primary" :loading="saving" @click="save"><i class="md-icon" v-if="!saving">done</i>{{ $t('save') }}</el-button>
        </div>
        <div class="fields-list" v-if="model">
            <dynamic-field v-for="field in fields"
                           :key="field.dbName"
                           v-model="model[field.friendlyName]"
                           :metadata="field" />
        </div>
    </div>
</template>


<script lang="ts">
    import Vue from 'vue';

    import dynamicField from '@/controls/fields/dynamic-field.vue';

    import { EntityType, FieldMetadata, MetadataProvider } from '@/utilities/fields/metadata';
    import { FieldName } from '@/utilities/fields/names';
    import { Person, PersonProvider } from '@/utilities/api/persons';
    import { EntityProvider, GenericEntityProvider } from '@/utilities/entities';
    import { Context } from '@/utilities/contex';
    import { EmailProvider, EmailType } from '@/utilities/api/email';

    export default Vue.extend({
        name: 'create-entity',
        components: {
            dynamicField
        },
        data() {
            return {
                saving: false,
                fields: [] as FieldMetadata[],
                model: null as Person | null,
            }
        },
        created() {
            this.initFields();
            this.initModel();
        },
        computed: {
            entityType(): EntityType {
                return +this.$route.query.entityType || EntityType.Undefined;
            },
            entityName(): string {
                return EntityProvider.getEntityName(this.entityType);
            },
            entityProvider(): GenericEntityProvider<any> {
                return EntityProvider.getProvider(this.entityType);
            },
            organisationId(): number {
                return +this.$route.query.organisationId || 0;
            },
            personId(): number {
                return +this.$route.query.personId || 0;
            },
            emailType(): EmailType {
                return +this.$route.query.emailType || EmailType.Outbound;
            },
            emailId(): number {
                return +this.$route.query.emailId || 0;
            },
            returnTo(): string {
                return this.$route.query.returnTo?.toString() || '';
            }
        },
        methods: {
            initFields() {
                const fieldNames = [...new Set([
                    ...EntityProvider.getStaticFields(this.entityType),
                    ...MetadataProvider.getEntityFields(this.entityType).filter(f => f.isMandatory && MetadataProvider.isFieldSupported(f)).map(f => f.dbName)
                ])];
                this.fields = fieldNames
                    .map(f => MetadataProvider.getFieldByName(f, this.entityType)!)
                    .filter(f => f);
            },
            initModel() {
                this.model = {
                    ...Object.fromEntries(this.fields.map(f => [f.friendlyName, null])),
                    ...EntityProvider.getEmptyModel(this.entityType, this.organisationId, this.personId, Context.user.userId)
                } as any;
            },
            async save() {
                if (!this.model) return;
                if (!MetadataProvider.validateEntity(this.model, this.fields)) return;
                this.saving = true;
                try {
                    const entityId = await this.entityProvider.save(this.model);
                    await EmailProvider.updateEntityLink({ entityId, entityType: this.entityType, id: this.emailId, type: this.emailType });
                    this.$router.push({ path: this.returnTo, query: { organisationId: this.organisationId.toString(), personId: this.personId.toString() } });
                } catch (e: any) {
                    console.error(e);
                    this.$notify.error({ title: this.$tc('error'), message: e.error || this.$tc('an_unexpected_error_occurred') });
                }
                this.saving = false;
            },
            back() {
                this.$router.back();
            }
        }
    });

</script>

<style scoped lang="scss">

</style>